import React, { useState } from "react";
import { withRouter } from "react-router-dom";
import { useAuth0 } from "../../auth0/react-auth0-wrapper";
import Layout from "../../components/layout/Layout";
import Container from "../../components/container/Container";
import Button from "../../components/button/Button";
import ISODateStringSeconds from "../../functions/ISODateStringSeconds";
import LanguageContext from "../../context/languagecontext";
import Styles from "./Passwordchange.module.scss";

const apiUri = process.env.REACT_APP_API_URI;

const PasswordChange = withRouter(({ history, ...props }) => {
  const { language } = React.useContext(LanguageContext);
  const initialFormState = {
    oldPassword: "",
    newPassword: "",
    newPasswordAgain: ""
  };

  const initialShowPasswordState = {
    oldPassword: "password",
    newPassword: "password",
    newPasswordAgain: "password"
  };

  const { getTokenSilently, user } = useAuth0();

  const [formData, setFormData] = useState(initialFormState);
  const [formValidation, setFormValidation] = useState({});
  const [newPasswordLength, setNewPasswordLength] = useState(false);
  const [newPasswordLowerCase, setNewPasswordLowerCase] = useState(false);
  const [newPasswordUpperCase, setNewPasswordUpperCase] = useState(false);
  const [newPasswordNumbers, setNewPasswordNumbers] = useState(false);
  const [newPasswordSpecialChars, setNewPasswordSpecialChars] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formResponse, setFormResponse] = useState();
  const [
    passwordSuccessfullyChanged,
    setPasswordSuccessfullyChanged
  ] = useState(false);
  const [showPassword, setShowPassword] = useState(initialShowPasswordState);

  const handleChange = e => {
    const { name, value } = e.target;

    setFormResponse();
    setFormData({ ...formData, [name]: value });

    if (value !== "") {
      setFormValidation({
        ...formValidation,
        [name]: undefined
      });
    }

    if (name === "newPassword") {
      const numbers = /[0-9]/;
      const lowerCase = /[a-z]/;
      const upperCase = /[A-Z]/;
      const specialChars = /[ !-/:-@[-`{-~]/;

      value.length >= 8
        ? setNewPasswordLength(true)
        : setNewPasswordLength(false);

      numbers.test(value)
        ? setNewPasswordNumbers(true)
        : setNewPasswordNumbers(false);
      upperCase.test(value)
        ? setNewPasswordUpperCase(true)
        : setNewPasswordUpperCase(false);
      lowerCase.test(value)
        ? setNewPasswordLowerCase(true)
        : setNewPasswordLowerCase(false);
      specialChars.test(value)
        ? setNewPasswordSpecialChars(true)
        : setNewPasswordSpecialChars(false);
    }
  };

  const handleClick = e => {
    const passwordField = [e.target.getAttribute("data-name")];
    let newValue = "";

    showPassword[passwordField] === "password"
      ? (newValue = "text")
      : (newValue = "password");

    setShowPassword({ ...showPassword, [passwordField]: newValue });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (formData.oldPassword === "") {
      setFormValidation({
        ...formValidation,
        oldPassword: `${
          language === "fr"
            ? "Veuillez entrer l'ancien mot de passe."
            : language === "it"
            ? "Si prega di inserire la vecchia password."
            : "Bitte altes Passwort eingeben."
        }`
      });
      return;
    }

    if (formData.newPassword === "") {
      setFormValidation({
        ...formValidation,
        newPassword: `${
          language === "fr"
            ? "Veuillez saisir un nouveau mot de passe."
            : language === "it"
            ? "Si prega di inserire una nuova password."
            : "Bitte neues Passwort eingeben."
        }`
      });
      return;
    }

    if (formData.newPasswordAgain === "") {
      setFormValidation({
        ...formValidation,
        newPasswordAgain: `${
          language === "fr"
            ? "Veuillez saisir à nouveau votre nouveau mot de passe."
            : language === "it"
            ? "Inserisci di nuovo la tua nuova password."
            : "Bitte neues Passwort erneut eingeben."
        }`
      });
      return;
    }

    if (
      [
        newPasswordNumbers,
        newPasswordUpperCase,
        newPasswordLowerCase,
        newPasswordSpecialChars
      ].filter(Boolean).length < 3
    ) {
      setFormValidation({
        ...formValidation,
        newPassword: `${
          language === "fr"
            ? "Le nouveau mot de passe ne remplit pas les conditions."
            : language === "it"
            ? "La nuova password non soddisfa le condizioni."
            : "Das neue Passwort entspricht nicht den Bedingungen."
        }`
      });
      return;
    }

    if (formData.newPasswordAgain !== formData.newPassword) {
      setFormValidation({
        ...formValidation,
        newPasswordAgain: `${
          language === "fr"
            ? "Les mots de passe ne correspondent pas."
            : language === "it"
            ? "Le password non corrispondono."
            : "Die Passwörter stimmen nicht überein."
        }`
      });
      return;
    }

    // Validation seems okay -> try to POST form
    if (
      formValidation &&
      !formValidation.oldPassword &&
      !formValidation.newPassword &&
      !formValidation.newPasswordAgain
    ) {
      setIsSaving(true);
      try {
        const token = await getTokenSilently();
        const response = await fetch(`${apiUri}/auth0/changepassword`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        });
        if (!response.ok) {
          throw Error(`${response.status}`);
        }
        const answer = await response.json();

        if (answer && answer.toManyAttempts) {
          setFormData(initialFormState);
          setIsSaving(false);
          setFormValidation({});
          setNewPasswordLength(false);
          setNewPasswordUpperCase(false);
          setNewPasswordLowerCase(false);
          setNewPasswordNumbers(false);
          setNewPasswordSpecialChars(false);
          setFormResponse({
            success: false,
            message: `${
              language === "fr"
                ? "Trop de tentatives ont échoué. La fonction de changement du mot de passe a été bloquée pour des raisons de sécurité jusqu'au " +
                  ISODateStringSeconds(new Date(answer.lockedUntil)) +
                  ". Veuillez réessayer après cette date."
                : language === "it"
                ? "Troppi tentativi falliti. La funzione per la modifica della password è stata bloccata per motivi di sicurezza fino al: " +
                  ISODateStringSeconds(new Date(answer.lockedUntil)) +
                  ". Dopo di che si prega di riprovare."
                : "Zu viele gescheiterte Versuche. Die Funktion zur Änderung des Passworts wurde aus Sicherheitsgründen gesperrt bis: " +
                  ISODateStringSeconds(new Date(answer.lockedUntil)) +
                  ". Bitte versuchen Sie es danach noch einmal."
            }`
          });
          return;
        }

        if (answer && answer.success) {
          setFormData(initialFormState);
          setIsSaving(false);
          setFormValidation({});
          setNewPasswordLength(false);
          setNewPasswordUpperCase(false);
          setNewPasswordLowerCase(false);
          setNewPasswordNumbers(false);
          setNewPasswordSpecialChars(false);
          setPasswordSuccessfullyChanged(true);
          setFormResponse({
            success: true,
            message: `${
              language === "fr"
                ? "Le mot de passe pour «" +
                  user.name +
                  "» a été modifié avec succès."
                : language === "it"
                ? "La password di «" +
                  user.name +
                  "» è stata modificata con successo."
                : "Das Passwort für «" +
                  user.name +
                  "» wurde erfolgreich geändert."
            }`
          });
        }
        if (answer && !answer.success) {
          setIsSaving(false);

          if (answer.oldPassword === false) {
            setFormData({ ...formData, oldPassword: "" });
            setFormValidation({
              ...formValidation,
              oldPassword: `${
                language === "fr"
                  ? "L'ancien mot de passe n'a pas été saisi correctement. Veuillez réessayer."
                  : language === "it"
                  ? "La vecchia password non è stata inserita correttamente. La preghiamo di riprovare."
                  : "Das alte Passwort wurde nicht korrekt eingegeben. Bitte versuchen Sie es noch einmal."
              }`
            });
            return;
          }

          if (answer.newPasswordAgain === false) {
            setFormData({ ...formData, oldPassword: "" });
            setFormValidation({
              ...formValidation,
              newPasswordAgain: `${
                language === "fr"
                  ? "Les mots de passe ne correspondent pas."
                  : language === "it"
                  ? "Le password non corrispondono."
                  : "Die Passwörter stimmen nicht überein."
              }`
            });
            return;
          }
          if (answer.newPassword === false) {
            setFormData({ ...formData, oldPassword: "" });
            setFormValidation({
              ...formValidation,
              newPassword: `${
                language === "fr"
                  ? "Le nouveau mot de passe ne remplit pas les conditions."
                  : language === "it"
                  ? "La nuova password non soddisfa le condizioni."
                  : "Das neue Passwort entspricht nicht den Bedingungen."
              }`
            });
            return;
          }
        }
      } catch (err) {
        setIsSaving(false);
        setFormResponse({
          success: false,
          message: `${
            language === "fr"
              ? "Le nouveau mot de passe pour «" +
                user.name +
                "» n'a pas pu être enregistré, veuillez réessayer plus tard. (" +
                err +
                ")"
              : language === "it"
              ? "La nuova password per «" +
                user.name +
                "» non è stata salvata, si prega di riprovare più tardi. (" +
                err +
                ")"
              : "Das neue Passwort für «" +
                user.name +
                "» konnte nicht gesichert werden, bitte versuchen Sie es später noch einmal. (" +
                err +
                ")"
          }`
        });
        return;
      }
    }
  };

  const renderContent = (
    <>
      <div className={Styles.header}>
        <h1 className={Styles.title}>{`${
          language === "fr"
            ? "Changer de mot de passe"
            : language === "it"
            ? "Cambiare la password"
            : "Passwort ändern"
        }`}</h1>
        <p className={Styles.subtitle}>{user.name}</p>
      </div>
      {!passwordSuccessfullyChanged && (
        <form className={Styles.formContainer} onSubmit={handleSubmit}>
          <div className={Styles.formSection}>
            <div className={Styles.formGroup}>
              <span>
                <input
                  className={`${Styles.formControl} ${formValidation &&
                    formValidation.oldPassword &&
                    Styles.formError}`}
                  type={showPassword.oldPassword}
                  name="oldPassword"
                  value={formData.oldPassword}
                  onChange={handleChange}
                  placeholder={
                    language === "fr"
                      ? "Entrez l'ancien mot de passe*"
                      : language === "it"
                      ? "Inserisci la vecchia password*"
                      : "Altes Passwort eingeben*"
                  }
                  required
                  autoComplete="off"
                  autoFocus
                />
                <span
                  data-name="oldPassword"
                  onClick={handleClick}
                  className={`${
                    showPassword.oldPassword !== "password"
                      ? Styles.pwVisible
                      : null
                  }`}
                />
              </span>
              {formValidation && formValidation.oldPassword && (
                <div className={Styles.formErrorMessage}>
                  {formValidation.oldPassword}
                </div>
              )}
            </div>

            <div className={Styles.formGroup}>
              <span>
                <input
                  className={`${Styles.formControl} ${formValidation &&
                    formValidation.newPassword &&
                    Styles.formError}`}
                  type={showPassword.newPassword}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder={
                    language === "fr"
                      ? "Saisir un nouveau mot de passe*"
                      : language === "it"
                      ? "Inserisci una nuova password*"
                      : "Neues Passwort eingeben*"
                  }
                  required
                  autoComplete="off"
                />
                <span
                  data-name="newPassword"
                  onClick={handleClick}
                  className={`${
                    showPassword.newPassword !== "password"
                      ? Styles.pwVisible
                      : null
                  }`}
                />
              </span>
              {formValidation && formValidation.newPassword && (
                <div className={Styles.formErrorMessage}>
                  {formValidation.newPassword}
                </div>
              )}
            </div>

            <div className={Styles.formGroup}>
              <span>
                <input
                  className={`${Styles.formControl} ${formValidation &&
                    formValidation.newPasswordAgain &&
                    Styles.formError}`}
                  type={showPassword.newPasswordAgain}
                  name="newPasswordAgain"
                  value={formData.newPasswordAgain}
                  onChange={handleChange}
                  placeholder={
                    language === "fr"
                      ? "Saisir à nouveau le nouveau mot de passe*"
                      : language === "it"
                      ? "Inserire nuovamente la nuova password*"
                      : "Neues Passwort erneut eingeben*"
                  }
                  required
                  autoComplete="off"
                />
                <span
                  data-name="newPasswordAgain"
                  onClick={handleClick}
                  className={`${
                    showPassword.newPasswordAgain !== "password"
                      ? Styles.pwVisible
                      : null
                  }`}
                />
              </span>
              {formValidation && formValidation.newPasswordAgain && (
                <div className={Styles.formErrorMessage}>
                  {formValidation.newPasswordAgain}
                </div>
              )}
            </div>
          </div>

          <div className={Styles.formSection}>
            <p className={Styles.aboveUlText}>
              {`${
                language === "fr"
                  ? "Votre nouveau mot de passe doit contenir les éléments suivants :"
                  : language === "it"
                  ? "La nuova password deve contenere quanto segue:"
                  : "Ihr neues Passwort muss folgendes enthalten:"
              }`}
            </p>
            <ul>
              <li
                className={`${
                  newPasswordLength ? Styles.requirementOkay : null
                }`}
              >
                {`${
                  language === "fr"
                    ? "Au moins 8 caractères"
                    : language === "it"
                    ? "Almeno 8 caratteri"
                    : "Mindestens 8 Zeichen lang"
                }`}
              </li>
              <li
                className={`${
                  [
                    newPasswordNumbers,
                    newPasswordUpperCase,
                    newPasswordLowerCase,
                    newPasswordSpecialChars
                  ].filter(Boolean).length >= 3
                    ? Styles.requirementOkay
                    : null
                }`}
              >
                {`${
                  language === "fr"
                    ? "Au moins 3 d'entre eux :"
                    : language === "it"
                    ? "Almeno 3 di:"
                    : "Mindestens 3 von:"
                }`}
              </li>
              <ul>
                <li
                  className={`${
                    newPasswordLowerCase ? Styles.requirementOkay : null
                  }`}
                >
                  {`${
                    language === "fr"
                      ? "Lettres minuscules (a-z)"
                      : language === "it"
                      ? "Lettere minuscole (a-z)"
                      : "Kleinbuchstaben (a-z)"
                  }`}
                </li>
                <li
                  className={`${
                    newPasswordUpperCase ? Styles.requirementOkay : null
                  }`}
                >
                  {`${
                    language === "fr"
                      ? "Lettres majuscules (A-Z)"
                      : language === "it"
                      ? "Lettere maiuscole (A-Z)"
                      : "Grossbuchstaben (A-Z)"
                  }`}
                </li>
                <li
                  className={`${
                    newPasswordNumbers ? Styles.requirementOkay : null
                  }`}
                >
                  {`${
                    language === "fr"
                      ? "Numéros (0-9)"
                      : language === "it"
                      ? "Numeri (0-9)"
                      : "Zahlen (0-9)"
                  }`}
                </li>
                <li
                  className={`${
                    newPasswordSpecialChars ? Styles.requirementOkay : null
                  }`}
                >
                  {`${
                    language === "fr"
                      ? "Caractères spéciaux (par exemple !@#)"
                      : language === "it"
                      ? "Caratteri speciali (ad es. !@#)"
                      : "Sonderzeichen (z.B. !@#)"
                  }`}
                </li>
              </ul>
            </ul>
          </div>

          <div className={Styles.formSection}>
            <div className={Styles.formGroup}>
              <div className={Styles.buttonGroup}>
                <Button
                  type="submit"
                  value={`${
                    isSaving === false
                      ? language === "fr"
                        ? "Sauvegarder"
                        : language === "it"
                        ? "Salva"
                        : "Speichern"
                      : language === "fr"
                      ? "Sauvegarder..."
                      : language === "it"
                      ? "Salva..."
                      : "Speichern..."
                  }`}
                  isSaving={isSaving}
                  disabled={
                    !newPasswordLength ||
                    [
                      newPasswordNumbers,
                      newPasswordUpperCase,
                      newPasswordLowerCase,
                      newPasswordSpecialChars
                    ].filter(Boolean).length < 3 ||
                    !formData.oldPassword ||
                    !formData.newPassword ||
                    !formData.newPasswordAgain ||
                    formValidation.oldPassword ||
                    formValidation.newPassword ||
                    formValidation.newPasswordAgain ||
                    isSaving
                  }
                />
                {!isSaving && (
                  <Button
                    link="/"
                    text={`${
                      language === "fr"
                        ? "Annuler"
                        : language === "it"
                        ? "Annullare"
                        : "Abbrechen"
                    }`}
                    abort={true}
                  />
                )}
              </div>
            </div>
          </div>
        </form>
      )}
      {formResponse && (
        <div
          className={`${Styles.formResponse} ${
            formResponse.success ? Styles.success : Styles.error
          }`}
        >
          {formResponse.message}
        </div>
      )}
      {passwordSuccessfullyChanged && (
        <div className={Styles.buttonContainerGoHome}>
          <Button
            link="/"
            text={`${
              language === "fr"
                ? "Retour à l'aperçu de la machine"
                : language === "it"
                ? "Torna alla panoramica delle macchine"
                : "Zurück zur Maschinen-Übersicht"
            }`}
          />
        </div>
      )}
    </>
  );

  return (
    <Layout showBackButton={true} backButtonLink="/">
      <Container>{renderContent}</Container>
    </Layout>
  );
});

export default PasswordChange;
